import Checkbox from "@mui/material/Checkbox";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Fragment, useMemo } from "react";
import { useLocalizedStrings } from "../../localization/LocalizedStringsProvider";
import { type Permission } from '@/models/account-management/Role';
import { useAppContext } from '../../AppContext';
import { ADMIN_ONLY_PERMISSIONS } from '../../permissions/permissions';

interface PermissionRowProps {
    id: string;
    name: string;
    access: number;
    onChange: (id: string, access: number) => void;
}

const PermissionRow = ({ id, name, access, onChange }: PermissionRowProps) => {    
    function flagsToBools(flags: number): {
        read: boolean;
        create: boolean;
        update: boolean;
        delete: boolean;
    } {
        return {
            read: (flags & 1) !== 0,
            create: (flags & 2) !== 0,
            update: (flags & 4) !== 0,
            delete: (flags & 8) !== 0
        };
    }

    const bools = useMemo(() => flagsToBools(access), [access]);

    function boolsToFlags(bools: {
        read: boolean;
        create: boolean;
        update: boolean;
        delete: boolean;
    }): number {
        let flags = 0;

        if (bools.read) flags |= 1;
        if (bools.create) flags |= 2;
        if (bools.update) flags |= 4;
        if (bools.delete) flags |= 8;

        return flags;
    }

    const handleReadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const flags = boolsToFlags({ read: event.target.checked, create: bools.create, update: bools.update, delete: bools.delete });
        onChange(id, flags);
    }

    const handleCreateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const flags = boolsToFlags({ read: bools.read, create: event.target.checked, update: bools.update, delete: bools.delete });
        onChange(id, flags);
    }

    const handleUpdateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const flags = boolsToFlags({ read: bools.read, create: bools.create, update: event.target.checked, delete: bools.delete });
        onChange(id, flags);
    }

    const handleDeleteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const flags = boolsToFlags({ read: bools.read, create: bools.create, update: bools.update, delete: event.target.checked });
        onChange(id, flags);
    }

    return (
        <TableRow key={id} sx={{ '& td, & th': { border: 0 } }}>
            <TableCell component="th" scope="row">
                {name}
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    checked={bools.read}
                    onChange={handleReadChange}
                    size="small"
                    sx={{ p: 0 }}
                    slotProps={{
                        input: { 'aria-label': `${name} read` },
                    }}
                />
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    checked={bools.create}
                    onChange={handleCreateChange}
                    size="small"
                    sx={{ p: 0 }}
                    slotProps={{
                        input: { 'aria-label': `${name} create` },
                    }}
                />
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    checked={bools.update}
                    onChange={handleUpdateChange}
                    size="small"
                    sx={{ p: 0 }}
                    slotProps={{
                        input: { 'aria-label': `${name} update` },
                    }}
                />
            </TableCell>
            <TableCell align="center">
                <Checkbox
                    checked={bools.delete}
                    onChange={handleDeleteChange}
                    size="small"
                    sx={{ p: 0 }}
                    slotProps={{
                        input: { 'aria-label': `${name} delete` },
                    }}
                />
            </TableCell>
        </TableRow>
    );
};

interface PermissionsProps {
    permissions: Permission[];
    onChange: (permissions: Permission[]) => void;
}

const Permissions = ({ permissions, onChange }: PermissionsProps) => {
    //const [rows, setRows] = useState(temp);
    const strings = useLocalizedStrings();
    const app = useAppContext();

    // TouchTab's own admin permissions are meaningless in a customer organization, so they are offered only
    // when the role being built belongs to the TouchTab org.
    //
    // Keyed on the org being VIEWED, not on who is looking. It used to ask isSuperAdmin(), which answers "am
    // I a TouchTab user?" — so a TouchTab super admin editing a CUSTOMER's role was still shown the admin
    // permissions and could grant them, on a role that can never reach those screens (TTD-159).
    //
    // Hidden, not removed: handleRowChanged still maps over the full list, so a role that already carries one
    // keeps it rather than losing it on the next save by someone who could not see it.
    const visible = useMemo(
        () => app.isViewingSuperAdminOrg() ? permissions : permissions.filter(p => !ADMIN_ONLY_PERMISSIONS.includes(p.id)),
        [permissions, app]);

    const handleRowChanged = (id: string, access: number) => {
        onChange(permissions.map((row) => row.id === id ? { ...row, access } : row));
        //setRows((prevRows) => prevRows.map((row) => row.id === id ? { ...row, access } : row));
    }

    // Group permissions by category for display: categories ordered by (sortOrder, name), ungrouped ("Other")
    // last, and permissions sorted alphabetically within each category.
    const OTHER = '__other__';
    const groups = useMemo(() => {
        const byKey = new Map<string, { key: string; name: string; sortOrder: number; items: Permission[] }>();
        for (const p of visible) {
            const key = p.categoryId ?? OTHER;
            let g = byKey.get(key);
            if (!g) {
                g = {
                    key,
                    name: p.categoryId ? (p.categoryName ?? '') : strings.other,
                    sortOrder: p.categoryId ? (p.categorySortOrder ?? 0) : Number.MAX_SAFE_INTEGER,
                    items: [],
                };
                byKey.set(key, g);
            }
            g.items.push(p);
        }
        const list = Array.from(byKey.values());
        list.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
        for (const g of list) g.items.sort((a, b) => a.name.localeCompare(b.name));
        return list;
    }, [visible, strings.other]);

    // With no categories defined yet, everything lands in "Other" — don't bother with a section header then.
    const showHeaders = !(groups.length === 1 && groups[0].key === OTHER);

    return (
        <TableContainer sx={{ maxHeight: 400 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>{strings.permission}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{strings.view}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{strings.add}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{strings.edit}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>{strings.delete}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map((group) => (
                        <Fragment key={group.key}>
                            {showHeaders && (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        sx={{ fontWeight: 700, bgcolor: 'action.hover', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}
                                    >
                                        {group.name}
                                    </TableCell>
                                </TableRow>
                            )}
                            {group.items.map((row) => (
                                <PermissionRow key={row.id} id={row.id} name={row.name} access={row.access} onChange={handleRowChanged} />
                            ))}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default Permissions;